import React, { useState } from 'react';
import { Package, Search, X, Calendar, Eye, MapPin } from 'lucide-react';

function UserOrdersList({ orders, onViewDetails }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter orders based on search
  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.id?.toString().includes(searchLower) ||
      order.address?.toLowerCase().includes(searchLower) ||
      order.district?.toLowerCase().includes(searchLower) ||
      order.status?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendiente' },
      'encamino': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Camino' },
      'completado': { bg: 'bg-green-100', text: 'text-green-700', label: 'Completado' },
      'cancelado': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Cancelado' },
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-medium font-montserrat ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-[#E6B43C]" />
          <h4 className="font-semibold text-slate-800 font-montserrat">
            Recojos Activos
          </h4>
          <span className="text-xs bg-[#E6B43C]/10 text-[#E6B43C] px-2 py-0.5 rounded-full font-medium font-montserrat">
            {orders.length}
          </span>
        </div>
      </div>

      {/* Search Bar */}
      {orders.length > 3 && (
        <div className="mb-3 relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por dirección, distrito, ID..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6B43C] focus:border-transparent font-montserrat"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-8">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 font-montserrat">
            {searchTerm ? 'No se encontraron recojos' : 'Sin recojos activos'}
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
          {filteredOrders.map((order) => (
            <div 
              key={order.id} 
              className="bg-white rounded-lg p-3 border border-slate-200 hover:border-[#E6B43C] transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-xs font-bold text-[#E6B43C] font-montserrat">
                    #{order.id}
                  </span>
                  {getStatusBadge(order.status)}
                </div>
                {onViewDetails && (
                  <button
                    onClick={() => onViewDetails(order.id, 'order')}
                    className="p-1.5 hover:bg-[#E6B43C]/10 rounded-lg transition-colors flex-shrink-0"
                    title="Ver detalles"
                  >
                    <Eye className="w-4 h-4 text-[#E6B43C]" />
                  </button>
                )}
              </div>

              <div className="space-y-1">
                {order.address && (
                  <p className="text-sm font-medium text-slate-900 truncate font-montserrat">
                    {order.address}
                  </p>
                )}

                {order.district && (
                  <p className="text-xs text-slate-600 font-montserrat">
                    📍 {order.district}
                  </p>
                )}

                {order.recojo_date && (
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-montserrat">{formatDate(order.recojo_date)}</span>
                  </div>
                )}

                {order.notes && (
                  <p className="text-xs text-slate-500 italic line-clamp-1 font-montserrat">
                    {order.notes}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserOrdersList;